<template>
  <div class="disp">
    <div class="disp__header">
      <h2 class="disp__titulo">
        Disponibilidad de carta
        <span class="disp__rango" v-if="rango">
          Del {{ fmtFecha(rango.desde) }} al {{ fmtFecha(rango.hasta) }}
        </span>
      </h2>
      <SelectorPeriodo :dias="30" @cambio="alCambiarPeriodo" />
    </div>

    <el-alert
      v-if="noDisponible"
      title="Historial de disponibilidad no disponible"
      description="La fuente de datos no está respondiendo. Volvé a intentar en unos minutos; si sigue igual, avisá a Operaciones."
      type="info"
      :closable="false"
      show-icon
      class="mb-3"
    ></el-alert>

    <div v-else-if="cargando" class="disp__cargando">
      Cargando historial…
      <span class="disp__cargando-nota">La primera carga de un periodo nuevo puede tardar unos segundos.</span>
    </div>

    <template v-else>
      <!-- Resumen del periodo: los mismos números que explican la card. -->
      <div class="resumen">
        <div class="resumen__item">
          <span class="resumen__valor">{{ fmtPct(resumen.disponibilidad_pct) }}</span>
          <span class="resumen__label">Carta activa promedio</span>
          <span class="resumen__pie">{{ resumen.dias_con_dato }} días medidos</span>
        </div>
        <div class="resumen__item">
          <span class="resumen__valor">{{ resumen.apagones_n }}</span>
          <span class="resumen__label">Platos caídos</span>
          <span class="resumen__pie">día completo, sumando todos los días</span>
        </div>
        <div class="resumen__item">
          <span class="resumen__valor">{{ resumen.platos_con_apagones }}</span>
          <span class="resumen__label">Platos afectados</span>
          <span class="resumen__pie">de {{ resumen.platos_distintos }} en carta</span>
        </div>
        <div class="resumen__item" v-if="resumen.peor_dia">
          <span class="resumen__valor">{{ fmtPct(resumen.peor_dia.disponibilidad_pct) }}</span>
          <span class="resumen__label">Peor día</span>
          <span class="resumen__pie">{{ fmtSoloFecha(resumen.peor_dia.fecha) }}</span>
        </div>
      </div>

      <div class="disp__toolbar">
        <div class="segmented">
          <button
            type="button"
            class="segmented__item"
            :class="{ 'segmented__item--activo': vista === 'platos' }"
            @click="vista = 'platos'"
          >
            Por plato ({{ platosFiltrados.length }})
          </button>
          <button
            type="button"
            class="segmented__item"
            :class="{ 'segmented__item--activo': vista === 'dias' }"
            @click="vista = 'dias'"
          >
            Por día ({{ dias.length }})
          </button>
        </div>

        <base-input
          v-if="vista === 'platos'"
          v-model="busqueda"
          prepend-icon="fas fa-search"
          placeholder="Buscar plato o categoría..."
          class="disp__buscar"
        ></base-input>

        <base-button type="success" size="sm" @click="descargar" :disabled="!hayFilas">
          <i class="fas fa-download"></i> Descargar Excel
        </base-button>
      </div>

      <div class="filtros" v-if="vista === 'platos'">
        <div class="filtros__campo">
          <select v-model="filtroCategoria" class="filtros__select">
            <option :value="null">Categoría: todas ({{ platos.length }})</option>
            <option v-for="o in opcionesCategoria" :key="o.valor" :value="o.valor">
              {{ o.etiqueta }} ({{ o.n }})
            </option>
          </select>
          <i class="fas fa-chevron-down filtros__chevron"></i>
        </div>
        <button v-if="filtroCategoria || busqueda" type="button" class="filtros__limpiar" @click="limpiar">
          <i class="fas fa-times"></i> Limpiar filtros
        </button>
      </div>

      <!-- POR PLATO -->
      <el-table
        v-if="vista === 'platos'"
        :data="platosPaginados"
        size="small"
        stripe
        empty-text="Ningún plato se cayó en el periodo"
      >
        <el-table-column type="expand">
          <template slot-scope="s">
            <div class="detalle">
              <div v-if="s.row.fechas_apagado.length">
                <strong>Días caído por completo:</strong>
                <span v-for="f in s.row.fechas_apagado" :key="'a' + f" class="chip chip--roja">
                  {{ fmtSoloFecha(f) }}
                </span>
              </div>
              <div v-if="s.row.fechas_parcial.length" class="detalle__parcial">
                <strong>Días caído en parte de los restaurantes:</strong>
                <span v-for="f in s.row.fechas_parcial" :key="'p' + f" class="chip chip--ambar">
                  {{ fmtSoloFecha(f) }}
                </span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Plato" min-width="260">
          <template slot-scope="s">{{ s.row.nombre || ('Plato #' + s.row.meal_id) }}</template>
        </el-table-column>
        <el-table-column label="Categoría" width="170" align="center" header-align="center">
          <template slot-scope="s">
            <span :class="{ 'celda-vacia': !s.row.categoria }">{{ s.row.categoria || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Días caído" width="115" align="center" header-align="center" sortable
          :sort-method="(a, b) => a.dias_apagado - b.dias_apagado">
          <template slot-scope="s">
            <span class="pill" :class="s.row.dias_apagado ? 'pill--roja' : 'pill--gris'">
              {{ s.row.dias_apagado }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Parcial" width="95" align="center" header-align="center">
          <template slot-scope="s">
            <span v-if="s.row.dias_parcial" class="pill pill--ambar">{{ s.row.dias_parcial }}</span>
            <span v-else class="celda-vacia">—</span>
          </template>
        </el-table-column>
        <el-table-column label="% de sus días" width="125" align="center" header-align="center">
          <template slot-scope="s">{{ fmtPct(s.row.pct_apagado) }}</template>
        </el-table-column>
        <el-table-column label="Horas perdidas" width="135" align="center" header-align="center" sortable
          :sort-method="(a, b) => a.horas_perdidas - b.horas_perdidas">
          <template slot-scope="s">{{ s.row.horas_perdidas }} h</template>
        </el-table-column>
        <el-table-column label="Último" width="105" align="center" header-align="center">
          <template slot-scope="s">{{ s.row.ultimo_apagado ? fmtSoloFecha(s.row.ultimo_apagado) : '—' }}</template>
        </el-table-column>
      </el-table>

      <!-- POR DÍA -->
      <el-table v-else :data="dias" size="small" stripe empty-text="Sin datos de disponibilidad en el periodo">
        <el-table-column label="Fecha" width="120" align="center" header-align="center">
          <template slot-scope="s">{{ fmtSoloFecha(s.row.fecha) }}</template>
        </el-table-column>
        <el-table-column label="Carta activa" width="130" align="center" header-align="center">
          <template slot-scope="s">
            <span v-if="s.row.disponibilidad_pct !== null" class="disp__pct">
              {{ fmtPct(s.row.disponibilidad_pct) }}
            </span>
            <span v-else class="celda-vacia">N/A</span>
          </template>
        </el-table-column>
        <el-table-column label="Platos caídos" width="130" align="center" header-align="center">
          <template slot-scope="s">
            <span v-if="s.row.estado === 'ok'" class="pill" :class="s.row.platos_apagados ? 'pill--roja' : 'pill--verde'">
              {{ s.row.platos_apagados }}
            </span>
            <span v-else class="celda-vacia">—</span>
          </template>
        </el-table-column>
        <el-table-column label="Parciales" width="110" align="center" header-align="center">
          <template slot-scope="s">
            <span v-if="s.row.estado === 'ok' && s.row.platos_parciales" class="pill pill--ambar">
              {{ s.row.platos_parciales }}
            </span>
            <span v-else class="celda-vacia">—</span>
          </template>
        </el-table-column>
        <el-table-column label="Platos en carta" width="130" align="center" header-align="center">
          <template slot-scope="s">{{ s.row.platos_n }}</template>
        </el-table-column>
        <el-table-column label="Estado" min-width="230">
          <template slot-scope="s">
            <span v-if="s.row.estado === 'ok'" class="celda-vacia">Medido</span>
            <span v-else class="disp__motivo">{{ s.row.motivo_sin_dato }}</span>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-if="vista === 'platos' && platosFiltrados.length > porPagina"
        class="disp__paginacion"
        layout="total, prev, pager, next"
        :total="platosFiltrados.length"
        :page-size="porPagina"
        :current-page.sync="paginaActual"
      ></el-pagination>

      <p v-if="truncado" class="disp__aviso">
        Se muestran los <strong>{{ tope }}</strong> platos con más caídas del período.
        Hubo más, pero no se listan. Acotá el rango para verlos.
      </p>

    </template>
  </div>
</template>

<script>
import { Alert, Table, TableColumn, Pagination } from 'element-ui'
import SelectorPeriodo from '@/components/Metrics/SelectorPeriodo.vue'
import MetricasCocinaGateway from '@/gateways/MetricasCocinaGateway'
import { descargarExcel } from '@/utils/excel'

/**
 * Que platos se cayeron de la carta OD, que dia y cuanto pesaron.
 *
 * Es el detalle HACIA ATRAS de la tarjeta "% Carta activa". Complementa el
 * historial de carta, que responde QUIEN pero solo desde que se activo el
 * registro: esta pantalla responde QUE y CUANDO, con los meses que el data
 * lake ya tenia guardados.
 *
 * Nunca envia una cocina: el backend la resuelve del token.
 */
export default {
  name: 'DisponibilidadHistorica',
  components: {
    SelectorPeriodo,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Pagination.name]: Pagination,
    [Alert.name]: Alert
  },
  data () {
    return {
      // Los pone SelectorPeriodo al montarse, antes de la primera carga.
      desde: null,
      hasta: null,
      cargando: true,
      noDisponible: false,
      vista: 'platos',
      dias: [],
      platos: [],
      resumen: {},
      rango: null,
      truncado: false,
      tope: null,
      busqueda: '',
      filtroCategoria: null,
      paginaActual: 1,
      porPagina: 20
    }
  },
  computed: {
    platosFiltrados () {
      const q = (this.busqueda || '').trim().toLowerCase()

      return this.platos.filter(p => {
        if (this.filtroCategoria && (p.categoria || 'Sin categoría') !== this.filtroCategoria) return false

        if (q) {
          const nombre = p.nombre || ('Plato #' + p.meal_id)
          if (![nombre, p.categoria].some(v => (v || '').toLowerCase().includes(q))) return false
        }

        return true
      })
    },
    /*
      Contador facetado, igual que en el historial de carta: el numero al lado
      de cada categoria es lo que vas a ver si la elegis.
    */
    opcionesCategoria () {
      const q = (this.busqueda || '').trim().toLowerCase()
      const cuenta = new Map()

      for (const p of this.platos) {
        if (q) {
          const nombre = p.nombre || ('Plato #' + p.meal_id)
          if (![nombre, p.categoria].some(v => (v || '').toLowerCase().includes(q))) continue
        }
        const v = p.categoria || 'Sin categoría'
        cuenta.set(v, (cuenta.get(v) || 0) + 1)
      }

      return [...cuenta.entries()]
        .map(([valor, n]) => ({ valor, etiqueta: valor, n }))
        .sort((a, b) => b.n - a.n || a.etiqueta.localeCompare(b.etiqueta))
    },
    platosPaginados () {
      const inicio = (this.paginaActual - 1) * this.porPagina
      return this.platosFiltrados.slice(inicio, inicio + this.porPagina)
    },
    hayFilas () {
      return this.vista === 'platos' ? this.platosFiltrados.length > 0 : this.dias.length > 0
    }
  },
  watch: {
    busqueda () { this.paginaActual = 1 },
    filtroCategoria () { this.paginaActual = 1 }
  },
  methods: {
    /* SelectorPeriodo emite tambien al montarse: esa es la primera carga. */
    alCambiarPeriodo (rango) {
      this.desde = rango.desde
      this.hasta = rango.hasta
      this.cargar()
    },
    limpiar () {
      this.filtroCategoria = null
      this.busqueda = ''
    },
    fmtFecha (iso) {
      if (!iso) return ''
      const p = iso.split('-')
      return p[2] + '/' + p[1] + '/' + p[0]
    },
    fmtSoloFecha (iso) {
      if (!iso) return '—'
      const p = iso.split('-')
      return p[2] + '/' + p[1]
    },
    /* null es "sin medición", nunca 0. Ver el contrato que manda el backend. */
    fmtPct (v) {
      return v === null || v === undefined ? 'N/A' : v + '%'
    },
    /*
      Baja las DOS vistas en un mismo archivo, cada una en su hoja. La tabla
      que estas mirando no cambia el archivo: si alguien lo va a mandar por
      correo, conviene que lleve todo y no la mitad.
    */
    descargar () {
      descargarExcel('disponibilidad-carta', [
        {
          nombre: 'Por plato',
          columnas: [
            { clave: 'plato', titulo: 'Plato', ancho: 40, tipo: 'texto' },
            { clave: 'categoria', titulo: 'Categoría', ancho: 20, tipo: 'texto' },
            { clave: 'dias_apagado', titulo: 'Días caído', ancho: 12, tipo: 'numero' },
            { clave: 'dias_parcial', titulo: 'Días parcial', ancho: 13, tipo: 'numero' },
            { clave: 'dias_en_carta', titulo: 'Días en carta', ancho: 14, tipo: 'numero' },
            { clave: 'pct_apagado', titulo: '% de sus días', ancho: 14, tipo: 'porcentaje' },
            { clave: 'horas_perdidas', titulo: 'Horas perdidas', ancho: 16, tipo: 'horas' },
            { clave: 'ultimo', titulo: 'Último caído', ancho: 14, tipo: 'texto' },
            { clave: 'fechas', titulo: 'Fechas caído', ancho: 50, tipo: 'texto' }
          ],
          filas: this.platosFiltrados.map(p => ({
            plato: p.nombre || ('Plato #' + p.meal_id),
            categoria: p.categoria || '',
            dias_apagado: p.dias_apagado,
            dias_parcial: p.dias_parcial,
            dias_en_carta: p.dias_en_carta,
            pct_apagado: p.pct_apagado,
            horas_perdidas: p.horas_perdidas,
            ultimo: p.ultimo_apagado ? this.fmtFecha(p.ultimo_apagado) : '',
            fechas: p.fechas_apagado.map(f => this.fmtFecha(f)).join(', ')
          }))
        },
        {
          nombre: 'Por día',
          columnas: [
            { clave: 'fecha', titulo: 'Fecha', ancho: 12, tipo: 'texto' },
            { clave: 'pct', titulo: 'Carta activa', ancho: 14, tipo: 'porcentaje' },
            { clave: 'apagados', titulo: 'Platos caídos', ancho: 14, tipo: 'numero' },
            { clave: 'parciales', titulo: 'Platos parciales', ancho: 16, tipo: 'numero' },
            { clave: 'en_carta', titulo: 'Platos en carta', ancho: 16, tipo: 'numero' },
            { clave: 'estado', titulo: 'Estado', ancho: 46, tipo: 'texto' }
          ],
          filas: this.dias.map(d => ({
            fecha: this.fmtFecha(d.fecha),
            // null y no 0 a proposito: un dia sin medicion tiene que quedar
            // como celda vacia, para que no ensucie un promedio en la planilla.
            pct: d.disponibilidad_pct,
            apagados: d.estado === 'ok' ? d.platos_apagados : null,
            parciales: d.estado === 'ok' ? d.platos_parciales : null,
            en_carta: d.platos_n,
            estado: d.estado === 'ok' ? 'Medido' : (d.motivo_sin_dato || 'Sin dato')
          }))
        }
      ])
    },
    cargar () {
      this.cargando = true
      this.noDisponible = false
      this.paginaActual = 1


      MetricasCocinaGateway.disponibilidad(this.desde, this.hasta)
        .then(payload => {
          if (payload.status === 'UNAVAILABLE') {
            this.noDisponible = true
            return
          }

          const r = payload.result || {}
          this.dias = r.dias || []
          this.platos = r.platos || []
          this.resumen = r.resumen || {}
          this.rango = r.rango || null
          this.truncado = !!r.truncado
          this.tope = r.tope || null
        })
        .catch(() => { this.noDisponible = true })
        .finally(() => { this.cargando = false })
    }
  }
}
</script>

<style scoped>
.disp { padding: 28px 34px; }
.disp__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 22px;
}
.disp__titulo { font-size: 28px; font-weight: 700; color: #1a202c; margin: 0; }
.disp__rango { font-size: 15px; font-weight: 400; color: #6b7684; margin-left: 14px; }
.disp__cargando { padding: 60px 0; text-align: center; color: #8a94a0; font-size: 14px; }
.disp__cargando-nota { display: block; margin-top: 8px; font-size: 12.5px; color: #b0b8c1; }

.resumen {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 22px;
}
.resumen__item {
  flex: 1;
  min-width: 170px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
}
.resumen__valor { font-size: 26px; font-weight: 700; color: #1a202c; line-height: 1.1; }
.resumen__label { font-size: 13px; font-weight: 600; color: #404040; }
.resumen__pie { font-size: 12px; color: #a3a3a3; }

.disp__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.disp__buscar { flex: 1; min-width: 220px; max-width: 340px; margin-bottom: 0; }
.disp__paginacion { margin-top: 18px; text-align: right; }
.disp__pct { font-weight: 600; }
.disp__motivo { font-size: 12.5px; color: #a16207; }
.celda-vacia { color: #c2c9d1; }

.detalle { padding: 6px 12px 10px 48px; line-height: 2.1; }
.detalle__parcial { margin-top: 6px; }
.detalle strong { font-size: 12.5px; color: #525252; margin-right: 8px; }
.chip {
  display: inline-block;
  margin-right: 5px;
  padding: 2px 8px;
  border-radius: 5px;
  font-size: 11.5px;
  font-weight: 600;
}
.chip--roja { background: #fef2f2; color: #dc2626; }
.chip--ambar { background: #fffbeb; color: #a16207; }

/* Mismos tokens que el historial de carta y el dashboard de Operaciones. */
.filtros { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 18px; }
.filtros__campo { position: relative; display: inline-flex; }
.filtros__select {
  appearance: none;
  -webkit-appearance: none;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 8px 30px 8px 12px;
  font-size: 14px;
  color: #404040;
  cursor: pointer;
  transition: border-color 0.15s;
}
.filtros__select:hover { border-color: #d4d4d4; }
.filtros__select:focus { outline: none; border-color: #008846; }
.filtros__chevron {
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  color: #a3a3a3;
  pointer-events: none;
}
.filtros__limpiar {
  background: none;
  border: none;
  padding: 8px 6px;
  font-size: 13px;
  color: #525252;
  cursor: pointer;
}
.filtros__limpiar:hover { color: #171717; }

.segmented {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f5f5f5;
  padding: 4px;
  border-radius: 8px;
}
.segmented__item {
  background: none;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #525252;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s, color 0.15s;
}
.segmented__item:hover { color: #171717; }
.segmented__item--activo,
.segmented__item--activo:hover { background: #171717; color: #fff; }

.pill {
  display: inline-block;
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 700;
}
.pill--verde { background: #e8f5ec; color: #01662e; }
.pill--roja { background: #fef2f2; color: #dc2626; }
.pill--ambar { background: #fffbeb; color: #a16207; }
.pill--gris { background: #f5f5f5; color: #737373; }

.disp__aviso {
  margin-top: 14px;
  padding: 10px 14px;
  border-left: 3px solid #fb6340;
  background: #fff8f5;
  border-radius: 4px;
  font-size: 12.5px;
  color: #5e4b45;
  line-height: 1.6;
}

/*
  Mismo arreglo que en el historial de carta: _element-tables.scss vuelve flex
  el ".cell" del header sin justify-content, asi que text-align no aplica y el
  titulo queda a la izquierda aunque header-align sea "center".
*/
::v-deep .el-table__header th.is-center .cell { justify-content: center; }
</style>
